import { Image } from '@sitecore-jss/sitecore-jss-nextjs';
import { useRouter } from 'next/router';

import { icon_chevron_right } from '@gn-audio/icons';

import { useFallbackTranslation } from '../../utils/hooks';
import { useComponentProps } from '../../utils/hooks/useComponentFields';
import { JabraIcon } from '../JabraIcon/JabraIcon';
import { LanguageSelectorProps, LanguageType, RegionType } from './LanguageSelector';

export const enum EcomCountryCode {
  Australia = 'AU',
  Austria = 'AT',
  Belgium = 'BE',
  Bulgaria = 'BG',
  Canada = 'CA',
  CzechRepublic = 'CZ',
  China = 'CN',
  Croatia = 'HR',
  Cyprus = 'CY',
  Denmark = 'DK',
  Estonia = 'EE',
  Finland = 'FI',
  France = 'FR',
  Germany = 'DE',
  GreatBritain = 'GB',
  Greece = 'GR',
  HongKong = 'HK',
  Hungary = 'HU',
  India = 'IN',
  Ireland = 'IE',
  Italy = 'IT',
  Japan = 'JP',
  Latvia = 'LV',
  Lithuania = 'LT',
  Luxembourg = 'LU',
  Malta = 'MT',
  Netherlands = 'NL',
  NewZealand = 'NZ',
  Norway = 'NO',
  Poland = 'PL',
  Portugal = 'PT',
  Romania = 'RO',
  Singapore = 'SG',
  Slovakia = 'SK',
  Slovenia = 'SI',
  Spain = 'ES',
  Sweden = 'SE',
  Switzerland = 'CH',
  UnitedStates = 'US',
  MiddleEastAndAfrica = 'MEA',
}

export type LanguageSelectorButton = {
  onClick: () => void;
  isActive?: boolean;
};

export const getCurrentLanguage = (
  locale: string | undefined,
  regions: RegionType[]
): LanguageType | undefined => {
  if (!locale) return;
  const countryCode = locale.split('-').pop();
  const countryName = getCountryName(countryCode?.toUpperCase());
  const matchingLanguages = regions
    .flatMap((el) => el.languages.results)
    .filter((el) => {
      return el.targetLocale.value.toLowerCase() === locale.toLowerCase();
    });
  if (matchingLanguages.length === 1) {
    return matchingLanguages[0];
  }

  return (
    matchingLanguages.find(
      (language) => language.countryName?.value?.toLowerCase() === countryName?.toLowerCase()
    ) ?? matchingLanguages[0]
  );
};

const getCountryName = (countryCode?: string) => {
  if (!countryCode) return;
  if (countryCode === EcomCountryCode.MiddleEastAndAfrica) return 'Middle East and Africa';
  try {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return regionNames.of(countryCode);
  } catch {
    return countryCode;
  }
};

export const LanguageSelectorButton = ({ onClick, isActive }: LanguageSelectorButton) => {
  const data = useComponentProps('LanguageSelector') as unknown as LanguageSelectorProps | null;
  const router = useRouter();
  const { t } = useFallbackTranslation();

  if (!data?.fields?.data?.item) {
    return null;
  }
  const region = getCurrentLanguage(router.locale, data?.fields?.data.item?.regions.results ?? []);

  if (!region) {
    return null;
  }

  const [targetLocale] = (region.targetLocale.value ?? '').split('-');
  const currency = region.currency.value ? ` / ${region.currency.value}` : '';

  return (
    <button
      aria-label={`${t('topmenu.LanguageMenu')}: ${targetLocale}${currency}`}
      onClick={onClick}
      className={`btn-top-menu w-full justify-between py-3 lg:w-auto lg:px-3 ${
        isActive && 'active'
      }`}
    >
      <div className="flex items-center lg:justify-center">
        <Image
          field={region.icon.jsonValue}
          width={24}
          height={24}
          alt={`${region?.countryName.value} country`}
        />
        <span className="ml-1 text-xs font-normal max-lg:hidden lg:inline">
          <span className="uppercase">{targetLocale}</span>
          {currency}
        </span>
        <span className="ml-4 text-xs font-normal lg:hidden">
          {`${region.languageName.value} / ${region.countryName.value}`}
        </span>
      </div>
      <JabraIcon className="icon h-3 lg:hidden" Icon={icon_chevron_right} />
    </button>
  );
};
